import {
  getGradientGeometry,
  isGradientGeometryV2,
} from '@web-stories-wp/patterns';
import * as React from 'react';
import { rgba } from 'polished';
import { hasGradient, type Pattern } from '@web-stories-wp/patterns';

type GradientBounds = { x: number; y: number; width: number; height: number };

type SvgTextGradientProps = {
  gradientId: string;
  pattern: Pattern;
  bounds?: GradientBounds;
  editorBounds?: GradientBounds;
};

export function SvgTextGradient({
  gradientId,
  pattern,
  bounds,
  editorBounds,
}: SvgTextGradientProps) {
  if (!hasGradient(pattern)) {
    return null;
  }

  if (pattern.type === 'linear') {
    const coords = isGradientGeometryV2(pattern)
      ? getGradientGeometry(pattern, bounds)
      : bounds
      ? getLinearGradientCoordsForBounds(pattern.rotation, bounds)
      : getLinearGradientCoords(pattern.rotation);
    return (
      <linearGradient
        id={gradientId}
        {...coords}
        data-gradient-pattern={encodeURIComponent(JSON.stringify(pattern))}
        data-gradient-bounds={editorBounds && JSON.stringify(editorBounds)}
      >
        {pattern.stops.map((stop, stopIndex) => (
          <stop
            key={`${gradientId}-stop-${stopIndex}`}
            offset={`${stop.position * 100}%`}
            stopColor={rgba(
              stop.color.r,
              stop.color.g,
              stop.color.b,
              (stop.color.a ?? 1) *
                (pattern.geometryVersion === 2 ? pattern.alpha ?? 1 : 1)
            )}
          />
        ))}
      </linearGradient>
    );
  }

  const radialProps = isGradientGeometryV2(pattern)
    ? getGradientGeometry(pattern, bounds)
    : bounds
    ? getRadialGradientProps(pattern, bounds)
    : {};
  return (
    <radialGradient
      id={gradientId}
      {...radialProps}
      data-gradient-pattern={encodeURIComponent(JSON.stringify(pattern))}
      data-gradient-bounds={editorBounds && JSON.stringify(editorBounds)}
    >
      {pattern.stops.map((stop, stopIndex) => (
        <stop
          key={`${gradientId}-stop-${stopIndex}`}
          offset={`${stop.position * 100}%`}
          stopColor={rgba(
            stop.color.r,
            stop.color.g,
            stop.color.b,
            (stop.color.a ?? 1) *
              (pattern.geometryVersion === 2 ? pattern.alpha ?? 1 : 1)
          )}
        />
      ))}
    </radialGradient>
  );
}

function getLinearGradientCoords(rotation = 0) {
  // Convert rotation (0–1) into radians (0–2π)
  const angleRadians = 2 * Math.PI * rotation;

  // Direction vector of the gradient
  const directionX = Math.sin(angleRadians);
  const directionY = -Math.cos(angleRadians); // negative because SVG Y grows downward

  // Scale so the gradient line reaches the bounding box edges
  const scaleToEdge =
    0.5 / Math.max(Math.abs(directionX), Math.abs(directionY));

  // Center of the bounding box
  const centerX = 0.5;
  const centerY = 0.5;

  return {
    x1: centerX + directionX * scaleToEdge,
    y1: centerY + directionY * scaleToEdge,
    x2: centerX - directionX * scaleToEdge,
    y2: centerY - directionY * scaleToEdge,
  };
}

function getLinearGradientCoordsForBounds(
  rotation: number | undefined,
  bounds: GradientBounds
) {
  const coords = getLinearGradientCoords(rotation);
  return {
    gradientUnits: 'userSpaceOnUse' as const,
    x1: bounds.x + bounds.width * coords.x1,
    y1: bounds.y + bounds.height * coords.y1,
    x2: bounds.x + bounds.width * coords.x2,
    y2: bounds.y + bounds.height * coords.y2,
  };
}

function getRadialGradientProps(pattern: Pattern, bounds: GradientBounds) {
  const center = pattern.center ?? { x: 0.5, y: 0.5 };
  const size = pattern.size ?? { w: 1, h: 1 };
  const cx = bounds.x + bounds.width * center.x;
  const cy = bounds.y + bounds.height * center.y;
  const rx = (bounds.width * (size.w ?? 1)) / 2;
  const ry = (bounds.height * (size.h ?? 1)) / 2;
  const r = Math.max(rx, ry);
  const scaleX = r ? rx / r : 1;
  const scaleY = r ? ry / r : 1;
  const gradientTransform =
    scaleX !== 1 || scaleY !== 1
      ? `translate(${cx} ${cy}) scale(${scaleX} ${scaleY}) translate(${-cx} ${-cy})`
      : undefined;

  return {
    gradientUnits: 'userSpaceOnUse' as const,
    cx,
    cy,
    r,
    ...(gradientTransform ? { gradientTransform } : {}),
  };
}
